/**
 * The front door: a phone number, then the code it receives.
 *
 * This is the screen someone who has never used the bot lands on, so it says what the
 * thing is before it asks for anything. Two steps rather than one form, because a code
 * field sitting empty and disabled next to a number field is a worse explanation of what
 * is about to happen than simply not showing it yet.
 *
 * The number was one free-text field seeded with the string "+91", and that was two
 * problems. Changing country meant editing a "+" the phone keypad does not offer, and
 * nothing was checked here at all — a mistyped number cost a reCAPTCHA round trip and an
 * SMS before `auth/invalid-phone-number` came back to say so. The country is a dropdown
 * now (`phone.ts`) and the number is validated before anything is sent.
 *
 * Errors surface on submit rather than on every keystroke. A number is wrong for most of
 * the time it is being typed, and a field that turns red at the second digit teaches
 * people to ignore it. Once a field has been wrong once it validates live, so the
 * message goes away at the keystroke that fixes it.
 */

import { useMemo, useRef, useState } from "react";
import type { ConfirmationResult } from "firebase/auth";
import { confirmPhoneCode, sendPhoneCode, type PhoneError } from "../auth.js";
import {
  COUNTRIES,
  checkPhone,
  countryByIso,
  digitsOnly,
  flagOf,
  formatNational,
  guessCountry,
  maxNationalLength,
  stripNational,
} from "../phone.js";

const MESSAGES: Record<PhoneError["kind"], string> = {
  badNumber: "Firebase would not accept that number. Check the country and try again.",
  tooMany: "Too many attempts from here. Wait a few minutes and try again.",
  badCode: "That code isn't right. Check the message and try again.",
  expired: "That code has expired. Send a new one.",
  failed: "Couldn't send the code.",
};

const CODE_LENGTH = 6;

export function SignIn() {
  const [iso, setIso] = useState(() => guessCountry(navigator.language));
  const [national, setNational] = useState("");
  const [code, setCode] = useState("");
  const [confirmation, setConfirmation] = useState<ConfirmationResult | null>(null);
  const [error, setError] = useState<PhoneError | null>(null);
  const [invalid, setInvalid] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // reCAPTCHA needs a real element to attach to, even when invisible.
  const recaptcha = useRef<HTMLDivElement>(null);
  const numberField = useRef<HTMLInputElement>(null);

  const country = countryByIso(iso);
  const check = useMemo(() => checkPhone(national, country), [national, country]);

  /*
   * Alphabetical by country name, which is how someone looks for one. Not by dial code,
   * and not with a hand-picked shortlist pinned above a divider: the wheel iOS renders
   * opens already scrolled to the current value, and typing a letter jumps to it on a
   * keyboard, so both ways in are faster than scanning a favourites block.
   */
  const options = useMemo(() => [...COUNTRIES].sort((a, b) => a.name.localeCompare(b.name)), []);

  const describe = (): string | null => {
    switch (check.problem) {
      case null:
        return null;
      case "empty":
        return "Enter your phone number.";
      case "short":
        return "That is too short to be a phone number.";
      case "long":
        return "That is too long for a phone number.";
      case "wrongLength": {
        // The counts are the whole value of saying anything at all here. Phrased around
        // the country name rather than in front of it — "A India number" is what you get
        // when a name is dropped into a sentence that assumed an article.
        const digits = digitsOnly(national).length;
        return `Numbers in ${country.name} have ${check.expected!.join(" or ")} digits. You have ${digits}.`;
      }
    }
  };

  const setNumber = (raw: string) => {
    const digits = stripNational(raw, country).slice(0, maxNationalLength(country));
    setNational(digits);
    // Once the field has been wrong, it corrects itself as you type rather than waiting
    // for another submit.
    if (invalid) setInvalid(checkPhone(digits, country).problem ? invalid : null);
  };

  const send = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!recaptcha.current || busy) return;

    if (!check.e164) {
      setInvalid(describe());
      numberField.current?.focus();
      return;
    }

    setBusy(true);
    setError(null);
    setInvalid(null);
    const result = await sendPhoneCode(check.e164, recaptcha.current);
    setBusy(false);
    if ("error" in result) return setError(result.error);
    setConfirmation(result.confirmation);
  };

  const confirm = async (typed = code) => {
    if (!confirmation || busy || typed.length < CODE_LENGTH) return;
    setBusy(true);
    setError(null);
    const failure = await confirmPhoneCode(confirmation, typed);
    setBusy(false);
    // Success needs no branch — onAuthStateChanged swaps this screen out.
    if (failure) setError(failure);
  };

  const setCodeDigits = (raw: string) => {
    const digits = digitsOnly(raw).slice(0, CODE_LENGTH);
    setCode(digits);
    // iOS fills the whole code from the SMS in one go, so waiting for a button press
    // after that is a step that exists only because the form has one.
    if (digits.length === CODE_LENGTH) void confirm(digits);
  };

  return (
    <div className="signin">
      <p className="signin-mark">altrtrack</p>
      <h1 className="display">Your training log.</h1>
      <p className="sub">
        Sign in with the number you log sets from. Every day, every set, and the score
        behind them.
      </p>

      {confirmation === null ? (
        <form className="signin-form" onSubmit={send} noValidate>
          <div className="field">
            <label className="label" htmlFor="phone-national">
              Phone number
            </label>
            <div className="phone-group">
              <select
                className="select"
                aria-label="Country calling code"
                value={iso}
                onChange={(e) => {
                  setIso(e.target.value);
                  setInvalid(null);
                }}
              >
                {options.map((c) => (
                  /* The closed control shows the flag and the code; the open list shows
                     the name too, because "+1" is four countries and a wheel of dial
                     codes is not a country picker. */
                  <option key={c.iso} value={c.iso} label={`${flagOf(c.iso)}\u2007+${c.dial}`}>
                    {flagOf(c.iso)} {c.name} +{c.dial}
                  </option>
                ))}
              </select>
              <input
                id="phone-national"
                ref={numberField}
                className="input"
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                value={formatNational(national, country.lengths?.[0])}
                onChange={(e) => setNumber(e.target.value)}
                onBlur={() => setInvalid(national ? describe() : null)}
                placeholder={formatNational("5".repeat(country.lengths?.[0] ?? 9), country.lengths?.[0])}
                aria-invalid={invalid !== null}
                aria-describedby="phone-help"
                enterKeyHint="send"
              />
            </div>
            <p className={invalid ? "field-error" : "field-hint"} id="phone-help" role="status">
              {invalid ?? `${country.name} · we'll text you a ${CODE_LENGTH}-digit code`}
            </p>
          </div>
          <button className="btn btn-primary" type="submit" disabled={busy}>
            {busy ? "Sending…" : "Send me a code"}
          </button>
        </form>
      ) : (
        <form
          className="signin-form"
          onSubmit={(e) => {
            e.preventDefault();
            void confirm();
          }}
          noValidate
        >
          <div className="field">
            <label className="label" htmlFor="phone-code">
              Code sent to +{country.dial} {formatNational(national, country.lengths?.[0])}
            </label>
            <input
              id="phone-code"
              className="input code-input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCodeDigits(e.target.value)}
              placeholder="000000"
              aria-label={`The ${CODE_LENGTH}-digit code from the message`}
              maxLength={CODE_LENGTH}
              autoFocus
              enterKeyHint="go"
            />
          </div>
          <button
            className="btn btn-primary"
            type="submit"
            disabled={busy || code.length < CODE_LENGTH}
          >
            {busy ? "Checking…" : "Sign in"}
          </button>
          <button
            className="btn"
            type="button"
            onClick={() => {
              setConfirmation(null);
              setCode("");
              setError(null);
            }}
          >
            Use a different number
          </button>
        </form>
      )}

      {error && (
        <div className="notice bad" role="alert">
          <b>{MESSAGES[error.kind]}</b>
          {error.kind === "failed" && (
            <p className="sub">
              Firebase said <code>{error.code}</code>. That is a project setting, not
              something you did.
            </p>
          )}
        </div>
      )}

      <p className="sub signin-alt">
        Already in a chat with the bot? Message it <b>dashboard</b> and it will send you a
        link that skips the code.
      </p>

      <div ref={recaptcha} />
    </div>
  );
}
